import React from 'react';
import { TemplateRenderProps } from './types';
import { FormFields, AddonsSelector, CouponSection, TrustAndSecurityBadge, SocialProofAndFomo } from './CheckoutCommonElements';
import { BillingCycleSelector, PlanFeatureList, TestimonialCard, PriceBreakdown } from './CheckoutSummaryElements';

export const SaasTemplate: React.FC<TemplateRenderProps> = ({
  plan,
  config,
  formData,
  onInputChange,
  billingCycle,
  onChangeCycle,
  selectedAddonIds,
  onToggleAddon,
  appliedCoupon,
  onApplyCoupon,
  onRemoveCoupon,
  onSubmitInfo,
  isCapturing,
  currencySymbol,
  addonsTotal,
  discountAmount,
  finalTotal,
}) => {
  return (
    <div
      className="min-h-full p-4 md:p-8 flex items-center justify-center transition-colors"
      style={{
        backgroundColor: config.backgroundColor || '#f8fafc',
        color: config.textColor || '#0f172a',
        fontFamily: config.fontFamily || 'Inter, sans-serif',
      }}
    >
      <div className="w-full max-w-4xl bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200/80 dark:border-slate-800 overflow-hidden grid grid-cols-1 md:grid-cols-12">
        {/* Left Column: Plan & Features */}
        <div className="md:col-span-5 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white p-6 md:p-8 flex flex-col justify-between relative overflow-hidden">
          <div className="space-y-5 relative z-10">
            <div className="flex items-center justify-between">
              {config.logoUrl && (
                <img src={config.logoUrl} alt="Logo" className="h-6 object-contain brightness-0 invert" />
              )}
              <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                SaaS Plan
              </span>
            </div>

            <div>
              <p className="text-xs font-bold text-indigo-400 uppercase tracking-wider">Checkout</p>
              <h2 className="text-2xl font-black tracking-tight mt-0.5">{plan.tier}</h2>
              {plan.description && (
                <p className="text-xs text-slate-300 mt-1.5 leading-relaxed">{plan.description}</p>
              )}
            </div>

            <BillingCycleSelector billingCycle={billingCycle} onChangeCycle={onChangeCycle} config={config} />

            <div className="pt-2">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">What is included:</p>
              <PlanFeatureList features={plan.features} textColorClass="text-slate-200" checkColorClass="text-indigo-400" />
            </div>

            {config.showTestimonial && <TestimonialCard testimonial={config.testimonial} />}
          </div>

          <div className="pt-4 mt-6 border-t border-white/10 relative z-10 flex items-end justify-between">
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase">Total Recurring</p>
              <p className="text-2xl font-black text-indigo-400">{currencySymbol}{finalTotal.toFixed(2)}</p>
            </div>
            <span className="text-[10px] text-slate-400 font-semibold">{billingCycle}</span>
          </div>
        </div>

        {/* Right Column: Checkout Form */}
        <div className="md:col-span-7 p-6 md:p-8 flex flex-col justify-between space-y-6">
          <div className="space-y-4">
            <div>
              <h1 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
                {config.headerMain || 'Complete Order'}
              </h1>
              {config.headerSub && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{config.headerSub}</p>
              )}
            </div>

            <SocialProofAndFomo config={config} />

            <form onSubmit={onSubmitInfo} className="space-y-4">
              <FormFields config={config} formData={formData} onInputChange={onInputChange} />
              <AddonsSelector addons={config.addons} selectedAddonIds={selectedAddonIds} onToggleAddon={onToggleAddon} currency={plan.currency} />
              <CouponSection config={config} appliedCoupon={appliedCoupon} onApplyCoupon={onApplyCoupon} onRemoveCoupon={onRemoveCoupon} />
              <PriceBreakdown plan={plan} billingCycle={billingCycle} addonsTotal={addonsTotal} discountAmount={discountAmount} finalTotal={finalTotal} currencySymbol={currencySymbol} />

              <button
                type="submit"
                disabled={isCapturing}
                className="w-full py-3.5 text-xs font-black uppercase tracking-wider text-white shadow-lg transition-all hover:opacity-95 active:scale-[0.99] disabled:opacity-50"
                style={{
                  backgroundColor: config.primaryColor || '#4f46e5',
                  borderRadius: config.buttonRadius || '8px',
                }}
              >
                {isCapturing ? 'Processing...' : config.buttonText || 'Continue to Payment'}
              </button>
            </form>
          </div>

          <div className="space-y-3">
            <TrustAndSecurityBadge config={config} />
            {config.footerText && (
              <p className="text-[10px] text-slate-400 text-center leading-tight">{config.footerText}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
